import axios from 'axios';

interface MonthlyTrend {
  month: string;
  filed: number;
  notices: number;
  demands: number;
}

// E-Proceedings Summary Types
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical' | 'administrative';

export interface SectionCount {
  section: string;
  count: number;
  riskLevel: RiskLevel;
}

export interface StatusCount {
  status: string;
  count: number;
  color: string;
}

export interface EprocSummary {
  needsAction: number;    // Open FYA proceedings
  overdue: number;        // Open FYA past due date
  dueSoon: number;        // Open FYA due within 7 days
  sectionBreakdown: SectionCount[];
  statusBreakdown: StatusCount[];
  last7Days: number;      // Proceedings with activity in last 7 days
  last30Days: number;     // Proceedings with activity in last 30 days
}

// Demands Summary Types (for Overview Dashboard)
export interface DemandStatusCount {
  status: string;
  count: number;
  color: string;
}

export interface DemandSectionCount {
  section: string;
  count: number;
}

export interface DemandsSummary {
  totalOutstanding: number;        // Sum of outstandingAmount
  activeDemands: number;           // Count of pending demands
  demandsAccruingInterest: number; // Count with interest accumulating
  statusBreakdown: DemandStatusCount[];
  sectionBreakdown: DemandSectionCount[];
  totalInterestAccrued: number;    // Sum of totalInterest
  demandsOlderThan6Months: number; // Count of aged demands
  last7Days: number;               // New/updated demands in last 7 days
  last30Days: number;              // New/updated demands in last 30 days
}

export interface TaxPortalOverview {
  totalClients: number;
  totalBalance: number;
  demandsRaised: number;
  ongoingProceedings: number;
  refundsDue: number;
  refundsIssued: number;
  percentageDemandsRaised: number;
  priorityTasks: {
    overdue: {
      pendingITRs: number;
      overdueProceedings: number;
    };
    upcoming: {
      pendingITRs: number;
      upcomingProceedings: number;
    };
  };
  noticeReport: {
    monthly: Array<{
      month: string;
      notices: number;
      responses: number;
    }>;
    quarterly: Array<{
      period: string;
      notices: number;
      responses: number;
    }>;
    daily: Array<{
      day: string;
      value: number;
    }>;
  };
  itrStatus: {
    totalBusinesses: {
      filed: number;
      notFiled: number;
      eVerified: number;
      pending: number;
      processed: number;
      defective: number;
      rejected: number;
      onHold: number;
      deadline: string;
    };
    auditedBusinesses: {
      filed: number;
      notFiled: number;
      eVerified: number;
      pending: number;
      processed: number;
      defective: number;
      rejected: number;
      onHold: number;
      deadline: string;
    };
    nonAuditedBusinesses: {
      filed: number;
      notFiled: number;
      eVerified: number;
      pending: number;
      processed: number;
      defective: number;
      rejected: number;
      onHold: number;
      deadline: string;
    };
  };
  taxAuditProgress: {
    [auditType: string]: {
      completed: number;
      total: number;
      deadline: string;
      name?: string;
      description?: string;
    };
  };
  taxCalendar: Array<{
    task: string;
    dueDate: string;
  }>;
  eprocSummary?: EprocSummary;
}

export interface TaxReturn {
  id: string;
  assessmentYear: string;
  clientName: string;
  currentStatus: string;
  filingDate: string | null;
  itrType: string | null;
  refundStatus: string | null;
  isNew?: boolean;
  isUpdated?: boolean;
  isNotFiled?: boolean; // Flag for clients without ITR entries
}

export interface TaxReturnListResponse {
  status: 'success' | 'error';
  returns: TaxReturn[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  error?: string;
}

export interface TaxReturnDetailResponse {
  acknowledgmentNo: string;
  acknowledgmentPath: string | null;
  aisDocumentPath: string | null;
  assessmentYear: string;
  clientId: string;
  clientName: string;
  clientType?: string | null;
  dateOfBirth?: string | null;
  filedBy: string;
  filingDate: string | null;
  filingSection: string;
  filingStatus: string | null;
  filingType: string;
  form26asPath: string | null;
  tisDocumentPath: string | null;
  id: string;
  intimationOrderPath: string | null;
  itr: string;
  itrFormPath: string | null;
  panId: string;
  parentPath: string;
  currentStatus: string;
  timeline: any[];
}

export interface TaxNotice {
  id: string;
  clientName: string;
  clientPanId: string;
  assessmentYear: string;
  proceedingName: string;
  proceedingType: string;
  proceedingReferenceId: string;
  proceedingLimitationDate: string;
  proceedingIssuedOn: string;
  proceedingStatus: string;
  noticePAN: string | null;
  lastActivity: string | null;
  isNew?: boolean;
  isUpdated?: boolean;
}

export interface TaxNoticeListResponse {
  status: 'success' | 'error';
  eprocs: TaxNotice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    totalRecords?: number;
  };
  error?: string;
}

export interface Notice {
  noticeId: string;
  noticeUnderSection: string;
  noticeDescription: string;
  issuedOn: string;
  responseDueDate: string;
  documentReferenceId: string;
  requestStatus: string;
  filePath: string;
  responseLapsedMessage?: string | null;
  responseSubmittedOnPortal?: boolean;
  portalResponseDate?: string | null;
  responses: any[];
}

export interface Demand {
  id: string;
  clientName: string;
  clientPanId: string;
  demandReferenceNumber: string;
  assessmentYear: string;
  proceedingName: string;
  proceedingType: string;
  proceedingReferenceId: string;
  proceedingLimitationDate: string;
  proceedingIssuedOn: string;
  originalDemandAmount: number;
  interestStartDate: string;
  totalInterest: number;
  outstandingDemand: number;
  filePath: string;
  demandId: string;
}

export interface TaxNoticeDetailResponse {
  id: string;
  clientId: string;
  clientName: string;
  panId: string;
  assessmentYear: string;
  proceedingName: string;
  proceedingType: string;
  proceedingReferenceId: string;
  proceedingLimitationDate: string;
  status: string;
  parentPath: string;
  closureOrder: string;
  closureOrderPath: string;
  closureOrders: Array<{
    id: string;
    closureOrder: string | null;
    fileName: string;
  }>;
  lastCheckedAt: string;
  timeline: [] | any;
  financialYear: string;
  notices: Notice[];
}

export interface TaxDemandDetailResponse {
  id: string;
  clientName: string;
  clientPan: string;
  clientType?: string | null;
  dateOfBirth?: string | null;
  demandReferenceNumber: string | null;
  assessmentYear: string;
  outstandingAmount: number;
  dateOfDemandRaised: string | null;
  currentStatus: string;
  sectionCode: string | null;
  filePath?: string;
  requestStatus?: string;
  rectificationRights?: string | null;
  timeline?: any[];
  totalInterest?: number | null;
  interestStartDate?: string | null;
  originalDemandAmount?: number | null;
  rateOfInterest?: string | null;
  timePeriod?: string | null;
}

export interface PendingResponse {
  id: string;
  noticeId: string;
  clientName: string;
  assessmentYear: string;
  noticeSection: string;
  noticeType: string;
  dueDate: string;
  status: string;
}

export interface PendingResponsesListResponse {
  status: 'success' | 'error';
  responses: PendingResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  error?: string;
}

export interface SubmitResponsePayload {
  responseCategory: 'EXPLANATION' | 'DOCUMENT' | 'PAYMENT' | 'OTHER';
  responseText: string;
}

export interface SubmitResponseResponse {
  status: 'success' | 'error';
  message: string;
  responseId: string;
  error?: string;
}

export interface ResponseHistoryItem {
  id: string;
  noticeId: string;
  clientName: string;
  responseCategory: string;
  responseText: string;
  submittedAt: string;
  submittedBy: string;
}

export interface ResponseHistoryResponse {
  status: 'success' | 'error';
  responses: ResponseHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  error?: string;
}

export interface TaxDemand {
  id: string;
  clientName: string;
  clientPan: string;
  assessmentYear: string;
  outstandingAmount: number;
  originalDemandAmount: number;
  totalInterest: number;
  dateOfDemandRaised: string | null;
  currentStatus: string;
  sectionCode: string | null;
  lastActivity: string | null;
  isNew?: boolean;
  isUpdated?: boolean;
}

export interface TaxDemandListResponse {
  status: 'success' | 'error';
  demands: TaxDemand[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    totalRecords?: number;
  };
  error?: string;
}

export interface NoticeResponse {
  eprocId: string;
  noticeId: string;
  clientName: string;
  clientPan: string;
  section: string;
  noticeDescription: string;
  responseCategory: 'COMPLIANCE' | 'OBJECTION' | 'APPEAL' | null;
  responseDueDate: string;
  responseText: string | null;
  submittedAt: string | null;
  hasNoticeLetter: boolean;
}

export interface GenerateResponsePayload {
  responseCategory: 'COMPLIANCE' | 'OBJECTION' | 'APPEAL';
  documentIds?: string[];
}

export type RefinementAction =
  | 'MAKE_FORMAL'
  | 'ADD_CITATIONS'
  | 'SHORTEN'
  | 'EXPAND'
  | 'SIMPLIFY_LANGUAGE'
  | 'ADD_PRECEDENTS'
  | 'STRENGTHEN_ARGUMENT'
  | 'CUSTOM';

export interface RefineResponsePayload {
  originalFullResponse: string;
  selectedText?: string;
  refinementAction: RefinementAction;
  customInstruction?: string;
  documentIds?: string[];
}

export interface RefineResponseResult {
  status: 'success' | 'error';
  eprocId: string;
  noticeId: string;
  refinedText: string;
  fullResponse: string;
  refinementAction: RefinementAction;
  tokensUsed: number;
  error?: string;
}

export interface SubmitNoticeResponsePayload {
  responseCategory: 'COMPLIANCE' | 'OBJECTION' | 'APPEAL';
  responseText: string;
  tokensUsed: number;
}

export interface NoticeResponseResult {
  status: 'success' | 'error';
  data?: NoticeResponse;
  message?: string;
  error?: string;
}

export interface GenerateResponseResult {
  status: 'success' | 'error';
  eprocId: string;
  noticeId: string;
  responseCategory: 'COMPLIANCE' | 'OBJECTION' | 'APPEAL';
  responseText: string;
  tokensUsed: number;
  message?: string;
  error?: string;
}

// Notice Analysis Types
export interface NoticeAnalysis {
  summary: string;
  keyDeadlines: Array<{
    description: string;
    date: string;
    urgency: 'high' | 'medium' | 'low';
  }>;
  requiredActions: string[];
  sectionsReferenced: Array<{
    section: string;
    relevance: string;
  }>;
  riskLevel: 'high' | 'medium' | 'low';
  riskExplanation: string;
  suggestedResponseType: 'COMPLIANCE' | 'OBJECTION' | 'APPEAL';
  suggestedResponseReason: string;
}

export interface NoticeAnalysisResponse {
  eprocId: string;
  noticeId: string;
  analysis: NoticeAnalysis;
  tokensUsed: number;
}

// Submitted Response Types (for viewing scraped responses from tax portal)
export interface SubmittedResponseFile {
  id: string;
  fileName: string;
  filePath: string;
  fileType: 'acknowledgement' | 'response';
}

export interface SubmittedResponseDetail {
  id: string;
  responseRemarks: string | null;
  responseSubmittedOn: string | null;
  responseType: string | null;
  responseFiledBy: string | null;
  acknowledgementStatus: string | null;
  files: SubmittedResponseFile[];
  // Notice context for display (for client-wide response lists)
  noticeId: string | null;
  noticeSection: string | null;
  noticeDescription: string | null;
}

export interface SubmittedResponsesResponse {
  clientId: string;  // Client ID for all responses
  clientName: string;
  responses: SubmittedResponseDetail[];
}

// Secure File Download Types
export type SecureFileType =
  | 'notice-letter'
  | 'closure-order'
  | 'demand-letter'
  | 'response-file'
  | 'acknowledgement'
  | 'itr-form'
  | 'itr-acknowledgment'
  | 'intimation-order'
  | 'form-26as'
  | 'ais-document'
  | 'tis-document';

/**
 * Generate a secure file download URL
 * This URL goes through our backend which validates authorization
 * and streams the file without exposing storage credentials
 */
export const getSecureFileUrl = (
  fileType: SecureFileType,
  entityId: string,
  fileId?: string
): string => {
  const params = new URLSearchParams({
    fileType,
    entityId,
  });
  if (fileId) {
    params.append('fileId', fileId);
  }
  return `/api/proxy/taxportal/tax-portal/files/download?${params.toString()}`;
};

const axiosInstance = axios.create({
  baseURL: '/api/proxy/',
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // A client-login user whose accountant's subscription lapsed mid-session: the
    // backend rejects every data request with this code, so send them to the paused page.
    if (
      typeof window !== 'undefined' &&
      error?.response?.status === 403 &&
      error?.response?.data?.code === 'CLIENT_ACCESS_PAUSED' &&
      !window.location.pathname.startsWith('/access-paused')
    ) {
      window.location.assign('/access-paused');
    }
    return Promise.reject(error);
  },
);

// Header context for the demand prepare-response view
export interface DemandResponseData {
  clientName: string | null;
  clientPan: string;
  demandReferenceNumber: string | null;
  assessmentYear: string;
  sectionCode: string | null;
  hasNoticeLetter: boolean;
}

// ITR Status categories based on timeline parsing
export interface ITRStatusBreakdown {
  // Filing stage
  notFiled: number;
  filed: number;
  dataNotAvailable: number;

  // Verification stage
  pendingEVerification: number;
  eVerified: number;

  // Processing stage
  underProcessing: number;
  processedRefundDue: number;
  processedNoRefund: number;

  // Refund outcome
  refundCredited: number;
  refundOnHold: number;
  refundFailed: number;
  refundAdjusted: number;

  // Other statuses
  demandRaised: number;
  defective: number;
  revised: number;
  other: number; // Catch-all for unknown statuses
}

// ITR Summary by Assessment Year
export interface ITRSummaryByYear {
  assessmentYear: string;
  totalVerifiedClients: number;
  statusBreakdown: ITRStatusBreakdown;
  // Legacy fields for backward compatibility
  filed: number;
  notFiled: number;
  eVerified: number;
  pending: number;
  processed: number;
  defective: number;
  rejected: number;
  onHold: number;
  refundIssued: number;
  refundDue: number;
  noRefund: number;
  hasDemand: number;
}

export const taxPortalService = {
  getOverview: async (): Promise<TaxPortalOverview> => {
    return await axiosInstance.get('taxportal/tax-portal/overview/');
  },

  // Get ITR summary aggregated by assessment year (server-side processing)
  getITRSummaryByYear: async (assessmentYear: string): Promise<ITRSummaryByYear> => {
    // Call the server-side API route that does the aggregation
    const response = await fetch(`/api/taxportal/itr-summary?assessmentYear=${encodeURIComponent(assessmentYear)}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch ITR summary: ${response.statusText}`);
    }

    const data = await response.json();
    const breakdown = data.statusBreakdown;

    // Calculate legacy counters from breakdown
    const processed = breakdown.processedNoRefund + breakdown.processedRefundDue +
                      breakdown.refundCredited + breakdown.refundOnHold +
                      breakdown.refundFailed + breakdown.refundAdjusted +
                      breakdown.demandRaised;
    const pending = breakdown.pendingEVerification + breakdown.eVerified + breakdown.underProcessing;

    // Return in the expected format with legacy fields
    return {
      assessmentYear: data.assessmentYear,
      totalVerifiedClients: data.totalVerifiedClients,
      statusBreakdown: breakdown,
      // Legacy fields for backward compatibility
      filed: breakdown.filed,
      notFiled: breakdown.notFiled,
      eVerified: breakdown.eVerified,
      pending,
      processed,
      defective: breakdown.defective,
      rejected: 0,
      onHold: breakdown.refundOnHold,
      refundIssued: breakdown.refundCredited,
      refundDue: breakdown.processedRefundDue,
      noRefund: breakdown.processedNoRefund,
      hasDemand: breakdown.demandRaised,
    };
  },

  getTaxReturns: async ({
    page = 1,
    limit = 10,
    search,
    assessmentYear,
    itrType,
    status,
    refundStatus,
    whatsNew,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    assessmentYear?: string;
    itrType?: string;
    status?: string;
    refundStatus?: string;
    whatsNew?: boolean;
  } = {}): Promise<TaxReturnListResponse> => {
    // Use the server-side API route that handles refundStatus filtering
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (assessmentYear) params.append('assessmentYear', assessmentYear);
    if (itrType) params.append('itrType', itrType);
    if (status) params.append('status', status);
    if (refundStatus) params.append('refundStatus', refundStatus);
    if (whatsNew) params.append('whatsNew', 'true');

    const response = await fetch(`/api/taxportal/return-status?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch tax returns: ${response.statusText}`);
    }
    return response.json();
  },

  getTaxReturnDetails: async (returnId: string): Promise<TaxReturnDetailResponse> => {
    return await axiosInstance.get(`taxportal/tax-portal/return-status/${returnId}/`);
  },
  getTaxEproceedings: async ({
    page = 1,
    limit = 10,
    month,
    year,
    dueBy,
    search,
    assessmentYear,
    proceedingType,
    proceedingStatus,
    section,
    noticePAN,
    whatsNew,
  }: {
    page?: number;
    limit?: number;
    month?: string;
    year?: string;
    dueBy?: string;
    search?: string;
    assessmentYear?: string;
    proceedingType?: string;
    proceedingStatus?: string;
    section?: string;
    noticePAN?: string;
    whatsNew?: boolean;
  } = {}): Promise<TaxNoticeListResponse> => {
    const params: Record<string, string | number | boolean> = { page, limit };
    if (month) params.month = month;
    if (year) params.year = year;
    if (dueBy) params.dueBy = dueBy;
    if (search) params.search = search;
    if (assessmentYear) params.assessmentYear = assessmentYear;
    if (proceedingType) params.proceedingType = proceedingType;
    if (proceedingStatus) params.proceedingStatus = proceedingStatus;
    if (section) params.section = section;
    if (noticePAN) params.noticePAN = noticePAN;
    if (whatsNew) params.whatsNew = true;
    return await axiosInstance.get('taxportal/tax-portal/eproceedings/', {
      params,
    });
  },

  getTaxEproceedingDetails: async (eprocId: string): Promise<TaxNoticeDetailResponse> => {
    return await axiosInstance.get(`taxportal/tax-portal/eproceedings/${eprocId}/`);
  },
  getPendingResponses: async (page = 1, limit = 10): Promise<PendingResponsesListResponse> => {
    return await axiosInstance.get('taxportal/tax-portal/responses/', {
      params: { page, limit },
    });
  },

  submitResponse: async (
    responseId: string,
    payload: SubmitResponsePayload,
  ): Promise<SubmitResponseResponse> => {
    return await axiosInstance.post(`taxportal/tax-portal/responses/${responseId}/`, payload);
  },

  getResponseHistory: async (page = 1, limit = 10): Promise<ResponseHistoryResponse> => {
    return await axiosInstance.get('taxportal/tax-portal/responses/history/', {
      params: { page, limit },
    });
  },

  getTaxDemands: async ({
    page = 1,
    limit = 10,
    search,
    assessmentYear,
    sectionCode,
    status,
    whatsNew,
    accruingInterest,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    assessmentYear?: string;
    sectionCode?: string;
    status?: string;
    whatsNew?: boolean;
    accruingInterest?: boolean;
  } = {}): Promise<TaxDemandListResponse> => {
    const params: Record<string, string | number | boolean> = { page, limit };
    if (search) params.search = search;
    if (assessmentYear) params.assessmentYear = assessmentYear;
    if (sectionCode) params.sectionCode = sectionCode;
    if (status) params.status = status;
    if (whatsNew) params.whatsNew = true;
    if (accruingInterest) params.accruingInterest = true;
    return await axiosInstance.get('taxportal/tax-portal/demands/', {
      params,
    });
  },

  getTaxDemandsDetails: async (demandId: string): Promise<TaxDemandDetailResponse> => {
    return await axiosInstance.get(`taxportal/tax-portal/demands/${demandId}/`);
  },

  getDemandsFilterOptions: async (): Promise<{
    assessmentYears: { value: string; label: string }[];
    sectionCodes: { value: string; label: string }[];
    statuses: { value: string; label: string }[];
  }> => {
    return await axiosInstance.get('taxportal/tax-portal/demands/filter-options/');
  },

  getDemandsSummary: async (): Promise<DemandsSummary> => {
    return await axiosInstance.get('taxportal/tax-portal/demands/summary/');
  },

  // Demand Response
  getDemandResponseData: async (demandId: string): Promise<DemandResponseData> => {
    return await axiosInstance.get(`taxportal/tax-portal/demands/${demandId}/response/`);
  },

  generateDemandResponse: async (demandId: string, payload: { responseCategory: string; documentIds?: string[] }) => {
    return await axiosInstance.post(`taxportal/tax-portal/demands/${demandId}/response/generate/`, payload);
  },

  refineDemandResponse: async (demandId: string, payload: {
    originalFullResponse: string;
    selectedText?: string;
    refinementAction: string;
    customInstruction?: string;
    responseCategory?: string;
    documentIds?: string[];
  }) => {
    return await axiosInstance.post(`taxportal/tax-portal/demands/${demandId}/response/refine/`, payload);
  },

  parseDemandDocumentForResponse: async (demandId: string, file: File) => {
    const formData = new FormData();
    formData.append('document', file);
    return await axiosInstance.post(`taxportal/tax-portal/demands/${demandId}/response/parse-document/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  removeDemandSupportingDocument: async (demandId: string, documentId: string) => {
    return await axiosInstance.delete(`taxportal/tax-portal/demands/${demandId}/response/document/${documentId}/`);
  },

  removeAllDemandSupportingDocuments: async (demandId: string) => {
    return await axiosInstance.delete(`taxportal/tax-portal/demands/${demandId}/response/documents/`);
  },

  getNoticeResponse: async (eprocId: string, noticeId: string): Promise<NoticeResponseResult> => {
    return await axiosInstance.get(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/response/`,
    );
  },

  generateNoticeResponse: async (
    eprocId: string,
    noticeId: string,
    payload: GenerateResponsePayload,
  ): Promise<GenerateResponseResult> => {
    return await axiosInstance.post(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/response/generate/`,
      payload,
    );
  },

  submitNoticeResponse: async (
    eprocId: string,
    noticeId: string,
    payload: SubmitNoticeResponsePayload,
  ): Promise<NoticeResponseResult> => {
    return await axiosInstance.post(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/response/submit/`,
      payload,
    );
  },

  refineNoticeResponse: async (
    eprocId: string,
    noticeId: string,
    payload: RefineResponsePayload,
  ): Promise<RefineResponseResult> => {
    return await axiosInstance.post(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/response/refine/`,
      payload,
    );
  },

  analyzeNotice: async (
    eprocId: string,
    noticeId: string,
  ): Promise<NoticeAnalysisResponse> => {
    return await axiosInstance.post(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/analyze/`,
    );
  },

  /**
   * Streaming notice analysis - sends progressive updates via SSE
   * @param eprocId - ID of the e-proceeding
   * @param noticeId - ID of the notice to analyze
   * @param callbacks - Event callbacks for different streaming events
   */
  analyzeNoticeStream: async (
    eprocId: string,
    noticeId: string,
    callbacks: {
      onStatus?: (message: string) => void;
      onSection?: (name: string, content: unknown) => void;
      onComplete: (response: NoticeAnalysisResponse) => void;
      onError: (error: string) => void;
    },
  ): Promise<void> => {
    console.log('[TaxPortal] Starting notice analysis stream:', { eprocId, noticeId });

    try {
      const response = await fetch(
        `/api/proxy/taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/analyze/stream/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for auth
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TaxPortal] Stream request failed:', response.status, errorText);
        callbacks.onError(`Failed to connect: ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('Streaming not supported');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (lines ending with \n\n)
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (data.type) {
                case 'status':
                  console.log('[TaxPortal] Status:', data.message);
                  callbacks.onStatus?.(data.message);
                  break;
                case 'section':
                  console.log('[TaxPortal] Section received:', data.name);
                  callbacks.onSection?.(data.name, data.content);
                  break;
                case 'complete':
                  console.log('[TaxPortal] Analysis stream complete');
                  callbacks.onComplete(data as NoticeAnalysisResponse);
                  break;
                case 'error':
                  console.error('[TaxPortal] Analysis stream error from server:', data.message);
                  callbacks.onError(data.message);
                  break;
              }
            } catch (parseError) {
              console.error('[TaxPortal] Failed to parse SSE data:', line, parseError);
            }
          }
        }
      }

      console.log('[TaxPortal] Analysis stream ended');
    } catch (error) {
      console.error('[TaxPortal] Analysis stream error:', error);
      callbacks.onError(error instanceof Error ? error.message : 'Unknown error');
    }
  },

  /**
   * Stream analyze a closure order using AI - sends progressive updates via SSE
   * @param recordId - ID of the specific tax_eprocs record with the closure order
   * @param callbacks - Event callbacks for different streaming events
   */
  analyzeClosureOrderStream: async (
    recordId: string,
    callbacks: {
      onStatus?: (message: string) => void;
      onSection?: (name: string, content: unknown) => void;
      onComplete: (response: NoticeAnalysisResponse) => void;
      onError: (error: string) => void;
    },
  ): Promise<void> => {
    console.log('[TaxPortal] Starting closure order analysis stream:', { recordId });

    try {
      const response = await fetch(
        `/api/proxy/taxportal/tax-portal/closure-order/${recordId}/analyze/stream/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TaxPortal] Closure order stream request failed:', response.status, errorText);
        callbacks.onError(`Failed to connect: ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('Streaming not supported');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (data.type) {
                case 'status':
                  console.log('[TaxPortal] Closure order status:', data.message);
                  callbacks.onStatus?.(data.message);
                  break;
                case 'section':
                  console.log('[TaxPortal] Closure order section received:', data.name);
                  callbacks.onSection?.(data.name, data.content);
                  break;
                case 'complete':
                  console.log('[TaxPortal] Closure order analysis stream complete');
                  callbacks.onComplete(data as NoticeAnalysisResponse);
                  break;
                case 'error':
                  console.error('[TaxPortal] Closure order stream error from server:', data.message);
                  callbacks.onError(data.message);
                  break;
              }
            } catch (parseError) {
              console.error('[TaxPortal] Failed to parse closure order SSE data:', line, parseError);
            }
          }
        }
      }

      console.log('[TaxPortal] Closure order analysis stream ended');
    } catch (error) {
      console.error('[TaxPortal] Closure order analysis stream error:', error);
      callbacks.onError(error instanceof Error ? error.message : 'Unknown error');
    }
  },

  // Supporting document methods for response generation
  parseDocumentForResponse: async (
    eprocId: string,
    noticeId: string,
    file: File,
  ): Promise<ParsedDocumentResponse> => {
    const formData = new FormData();
    formData.append('document', file);
    return await axiosInstance.post(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/response/parse-document/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  },

  removeDocumentForResponse: async (
    eprocId: string,
    noticeId: string,
    documentId: string,
  ): Promise<void> => {
    return await axiosInstance.delete(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/response/document/${documentId}/`,
    );
  },

  removeAllDocumentsForResponse: async (
    eprocId: string,
    noticeId: string,
  ): Promise<{ removedCount: number }> => {
    return await axiosInstance.delete(
      `taxportal/tax-portal/eproceedings/${eprocId}/${noticeId}/response/documents/`,
    );
  },

  // Get submitted responses for a notice (scraped from tax portal)
  getSubmittedResponses: async (noticeId: string): Promise<SubmittedResponsesResponse> => {
    return await axiosInstance.get(
      `taxportal/tax-portal/eproceedings/${noticeId}/submitted-responses/`,
    );
  },
};

// Supporting document types
export interface ParsedDocumentResponse {
  status: 'success' | 'error';
  data: {
    documentId: string;
    fileName: string;
    fileType: 'pdf' | 'docx' | 'xlsx';
    characterCount: number;
  };
}

// What's New Types
export interface WhatsNewCounts {
  returns: { new: number; updated: number };
  proceedings: { new: number; updated: number };
  demands: { new: number; updated: number };
}

export interface WhatsNewResponse {
  lastLoginAt: string | null;
  counts: WhatsNewCounts;
}

export interface MarkAsSeenRequest {
  recordType: 'returns' | 'proceedings' | 'demands';
  recordId: string;
}

// What's New Service Methods
export const whatsNewService = {
  /**
   * Get counts of new and updated records since last login
   */
  getWhatsNew: async (): Promise<WhatsNewResponse> => {
    return await axiosInstance.get('taxportal/tax-portal/whats-new/');
  },

  /**
   * Mark a record as seen (updates lastCheckedAt timestamp)
   * Called automatically when viewing a detail page
   */
  markAsSeen: async (recordType: 'returns' | 'proceedings' | 'demands', recordId: string): Promise<{ success: boolean }> => {
    return await axiosInstance.post('taxportal/tax-portal/mark-seen/', {
      recordType,
      recordId,
    });
  },

  /**
   * Get total count of new items for sidebar badges
   */
  getTotalNewCount: (counts: WhatsNewCounts): number => {
    return (
      counts.returns.new +
      counts.returns.updated +
      counts.proceedings.new +
      counts.proceedings.updated +
      counts.demands.new +
      counts.demands.updated
    );
  },

  /**
   * Get total count for a specific category (new + updated)
   */
  getCategoryCount: (counts: WhatsNewCounts, category: 'returns' | 'proceedings' | 'demands'): number => {
    return counts[category].new + counts[category].updated;
  },
};
